/**
 * ModelProviderConfigPage
 *
 * Admin page for configuring AI model provider credentials.
 * - ADK (adk): fixed section — project_id + region for the ADK runner
 * - API Provider Keys: dynamic — Azure OpenAI, Anthropic, OpenAI (api_key only)
 *
 * Route: /settings/models/providers  (admin-only)
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Cloud, Info } from "lucide-react";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { CompactButton } from "../../components/Button/Buttons";
import Input from "../../components/Inputs/Input";
import Select from "../../components/Inputs/Select";
import Loader from "../../components/Loader/Loader";
import CredentialField from "../../components/Settings/CredentialField";
import useFeedback from "../../hooks/useFeedback";
import {
  useGetModelProvider,
  useListModelProviders,
  useUpdateModelProvider,
  useDeleteModelProvider,
} from "../../api/queryHooks";
import UserAuthStore from "../../store/userStore";

// ─── Provider registry ────────────────────────────────────────────────────────
const PROVIDER_LABELS = {
  azure:        "Azure",
  anthropic:    "Anthropic",
  openai:       "OpenAI",
  gemini:       "Google Gemini",
  mistral:      "Mistral",
  groq:         "Groq",
  together:     "Together AI",
  cohere:       "Cohere",
  xai:          "xAI (Grok)",
  ollama:       "Ollama",
};
const labelFor = (id) => PROVIDER_LABELS[id] || id;

const KNOWN_PROVIDER_OPTIONS = [
  { value: "azure",        label: "Azure"          },
  { value: "anthropic",    label: "Anthropic"      },
  { value: "openai",       label: "OpenAI"          },
  { value: "gemini",       label: "Google Gemini"  },
  { value: "mistral",      label: "Mistral"         },
  { value: "groq",         label: "Groq"            },
  { value: "together",     label: "Together AI"    },
  { value: "cohere",       label: "Cohere"          },
  { value: "xai",          label: "xAI (Grok)"     },
  { value: "ollama",       label: "Ollama"          },
  { value: "_custom",      label: "Custom…"         },
];

// Providers that are keyless — no API key required, but base_url is mandatory.
const KEYLESS_PROVIDER_IDS = new Set(["ollama"]);

const PROVIDER_KEY_FIELDS = [
  { key: "api_key", label: "API Key", placeholder: "Paste API key" },
];

// ─── ADK section ─────────────────────────────────────────────────────────────
const AdkSection = ({ children, onSave, saving, dirty }) => (
  <div className="bg-surface-card dark:bg-surface-card border border-edge-gray-200 dark:border-edge-panel-border rounded-xl p-5 space-y-4">
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="photon-settings-icon w-9 h-9 rounded-lg bg-fill-indigo-50 dark:bg-fill-hex-1e293b flex items-center justify-center flex-shrink-0">
          <Cloud className="w-4 h-4 text-ink-primary dark:text-ink-accent" />
        </div>
        <div>
          <p className="text-[13px] font-semibold text-ink-gray-900 dark:text-ink-gray-50">ADK</p>
          <p className="text-[11px] text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
            GCP project and region required by the ADK agent runner. Vertex AI regions per model are configured in Model Config.
          </p>
        </div>
      </div>
      <CompactButton onClick={onSave} disabled={!dirty || saving} className="flex-shrink-0">
        {saving ? "Saving…" : "Save"}
      </CompactButton>
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

// ─── Field row ────────────────────────────────────────────────────────────────
const Field = ({ label, hint, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
      {label}
    </label>
    {children}
    {hint && <span className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">{hint}</span>}
  </div>
);

// ─── Empty form defaults ──────────────────────────────────────────────────────
const VERTEX_EMPTY = { project_id: "", region: "" };

// ─── Page ─────────────────────────────────────────────────────────────────────
const ModelProviderConfigPage = () => {
  const { showFeedback } = useFeedback();
  const orgId = UserAuthStore((s) => s.orgId);
  const userId = UserAuthStore((s) => s.userId);

  // ── ADK state ─────────────────────────────────────────────────────────────
  const [vertexForm, setVertexForm]     = useState(VERTEX_EMPTY);
  const [vertexDirty, setVertexDirty]   = useState(false);
  const [vertexSaving, setVertexSaving] = useState(false);

  // ── Add-provider form state ─────────────────────────────────────────────
  const [adding, setAdding]       = useState(false);
  const [addForm, setAddForm]     = useState({ provider_id: "", custom_id: "", api_key: "", base_url: "" });
  const [addSaving, setAddSaving] = useState(false);

  // ── Data fetching ─────────────────────────────────────────────────────────
  const { data: vertexData, isLoading: vertexLoading } = useGetModelProvider(orgId, "adk");
  const { data: listData,   isLoading: listLoading   } = useListModelProviders(orgId);
  const updateProvider = useUpdateModelProvider();
  const deleteProvider = useDeleteModelProvider();

  // ── Populate ADK form ──────────────────────────────────────────────────────
  useEffect(() => {
    if (vertexData) {
      setVertexForm({
        project_id: vertexData.project_id ?? "",
        region:     vertexData.region     ?? "",
      });
    }
  }, [vertexData]);

  // ── Configured providers (excluding adk) ──────────────────────────────────
  const configuredProviders = useMemo(
    () => (listData?.providers ?? []).filter((p) => p.model_provider_id !== "adk" && p.configured),
    [listData]
  );

  // ── Options for the Add form (hide already-configured known providers) ────
  const addableOptions = useMemo(() => {
    const configuredIds = new Set(configuredProviders.map((p) => p.model_provider_id));
    return [
      { value: "", label: "Select a provider…", disabled: true },
      ...KNOWN_PROVIDER_OPTIONS.filter(
        (p) => p.value === "_custom" || !configuredIds.has(p.value)
      ),
    ];
  }, [configuredProviders]);

  // ── Derived values for the Add form ──────────────────────────────────────
  const resolvedProviderId =
    addForm.provider_id === "_custom"
      ? addForm.custom_id.trim().toLowerCase().replace(/\s+/g, "_")
      : addForm.provider_id;
  const isKeylessProvider = KEYLESS_PROVIDER_IDS.has(resolvedProviderId);
  const requiresBaseUrl = addForm.provider_id === "_custom" || isKeylessProvider;
  const canAddSave = resolvedProviderId.length > 0
    && (isKeylessProvider || addForm.api_key.trim().length > 0)
    && (!requiresBaseUrl || addForm.base_url.trim().length > 0);

  // ── ADK handlers ──────────────────────────────────────────────────────────
  const setVertex = useCallback(
    (field) => (e) => {
      setVertexForm((prev) => ({ ...prev, [field]: e.target.value }));
      setVertexDirty(true);
    },
    []
  );

  const saveVertex = async () => {
    setVertexSaving(true);
    try {
      await updateProvider.mutateAsync({ orgId, userId, modelProviderId: "adk", data: vertexForm });
      setVertexDirty(false);
      showFeedback("ADK config saved.", "success");
    } catch {
      showFeedback("Failed to save ADK config.", "error");
    } finally {
      setVertexSaving(false);
    }
  };

  // ── Add provider handler ─────────────────────────────────────────────────
  const handleAddSave = async () => {
    setAddSaving(true);
    try {
      const providerData = {};
      if (!isKeylessProvider) providerData.api_key = addForm.api_key.trim();
      if (addForm.base_url.trim()) providerData.base_url = addForm.base_url.trim();
      await updateProvider.mutateAsync({
        orgId, userId, modelProviderId: resolvedProviderId,
        data: providerData,
      });
      setAdding(false);
      setAddForm({ provider_id: "", custom_id: "", api_key: "", base_url: "" });
      showFeedback(`${labelFor(resolvedProviderId)} API key saved.`, "success");
    } catch {
      showFeedback("Failed to save provider key.", "error");
    } finally {
      setAddSaving(false);
    }
  };

  const loading = vertexLoading || listLoading;

  return (
    <SettingsPageShell
      title="Provider Config"
      description="Configure credentials and endpoint settings for AI model providers. These values are used by Photon AI when routing requests to each provider."
      maxWidth="4xl"
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="space-y-5">
          {/* ── ADK ──────────────────────────────────────────────── */}
          <AdkSection onSave={saveVertex} saving={vertexSaving} dirty={vertexDirty}>
            <Field label="Project ID" hint="The GCP project ID with Vertex AI enabled.">
              <Input
                value={vertexForm.project_id}
                onChange={setVertex("project_id")}
                placeholder="my-gcp-project"
              />
            </Field>
            <Field label="Region" hint="Default region for Vertex AI API calls (e.g. us-central1).">
              <Input
                value={vertexForm.region}
                onChange={setVertex("region")}
                placeholder="us-central1"
              />
            </Field>
          </AdkSection>

          {/* ── API Provider Keys ─────────────────────────────────── */}
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[13px] font-semibold text-ink-gray-900 dark:text-ink-gray-50">API Provider Keys</p>
                <p className="text-[11px] text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
                  Third-party AI provider API keys used for model routing.
                </p>
              </div>
              {!adding && (
                <CompactButton onClick={() => setAdding(true)} className="flex-shrink-0">
                  + Add Provider
                </CompactButton>
              )}
            </div>

            {/* Configured providers */}
            {configuredProviders.map((p) => (
              <>
                <CredentialField
                  key={p.model_provider_id}
                  title={labelFor(p.model_provider_id)}
                  fields={PROVIDER_KEY_FIELDS}
                  description={p.model_provider_id === "azure" ? (
                    <div className="flex gap-2 rounded-lg bg-fill-blue-50 dark:bg-fill-hex-1e3a5f/30 border border-edge-blue-200 dark:border-edge-blue-700/40 px-3 py-2.5">
                      <Info className="w-3.5 h-3.5 text-ink-blue-700 dark:text-ink-accent flex-shrink-0 mt-px" />
                      <div className="text-[11px] text-ink-blue-800 dark:text-ink-blue-300 leading-relaxed space-y-1.5">
                        <p>This key covers authentication only. Per-model routing is configured on each model document in <strong>Model Config</strong>.</p>
                        <p>
                          For Azure AI Foundry, set these fields on the model doc:
                        </p>
                        <ul className="space-y-0.5 pl-1">
                          <li><span className="font-mono bg-fill-blue-100 dark:bg-fill-hex-1e3a5f px-1 py-px rounded text-[10px]">base_url</span>{" "}<span className="font-mono bg-fill-blue-100 dark:bg-fill-hex-1e3a5f px-1 py-px rounded text-[10px]">&quot;https://&lt;resource&gt;.services.ai.azure.com/openai/v1/&quot;</span></li>
                          <li><span className="font-mono bg-fill-blue-100 dark:bg-fill-hex-1e3a5f px-1 py-px rounded text-[10px]">use_responses_api</span>{" "}<span className="font-mono bg-fill-blue-100 dark:bg-fill-hex-1e3a5f px-1 py-px rounded text-[10px] text-ink-violet-600 dark:text-ink-violet-300">true</span>{" "}<span className="text-[10px] opacity-70">(boolean — Responses API only; omit or set <span className="font-mono font-bold">false</span> for Chat Completions)</span></li>
                        </ul>
                      </div>
                    </div>
                  ) : undefined}
                  onFetch={async () => ({ configured: true, last4: p.last4 ?? "" })}
                  onSave={async (form) => {
                    const result = await updateProvider.mutateAsync({
                      orgId, userId, modelProviderId: p.model_provider_id,
                      data: { api_key: form.api_key.trim() },
                    });
                    return { configured: true, last4: result?.last4 ?? "" };
                  }}
                  onRevoke={async () => {
                    await deleteProvider.mutateAsync({ orgId, modelProviderId: p.model_provider_id });
                  }}
                />
                {p.base_url && (
                  <p className="text-[11px] text-ink-gray-500 dark:text-ink-gray-400 -mt-2 px-1">
                    Custom endpoint: <span className="font-mono text-ink-gray-700 dark:text-ink-gray-300">{p.base_url}</span>
                  </p>
                )}
              </>
            ))}

            {/* Empty state */}
            {configuredProviders.length === 0 && !adding && (
              <p className="text-[12px] text-ink-gray-400 dark:text-ink-gray-500 py-1">
                No provider keys configured yet.
              </p>
            )}

            {/* Inline Add Provider form */}
            {adding && (
              <div className="flex flex-col gap-3 rounded-lg border border-edge-gray-200 dark:border-edge-gray-700 p-4">
                <p className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">Add Provider</p>
                <Field label="Provider">
                  <Select
                    value={addForm.provider_id}
                    onChange={(v) => setAddForm((prev) => ({ ...prev, provider_id: v, custom_id: "" }))}
                    options={addableOptions}
                  />
                </Field>
                {addForm.provider_id === "_custom" && (
                  <Field label="Provider ID" hint="Lowercase, underscore-separated (e.g. mistral, cohere).">
                    <Input
                      value={addForm.custom_id}
                      onChange={(e) => setAddForm((prev) => ({ ...prev, custom_id: e.target.value }))}
                      placeholder="my_provider"
                    />
                  </Field>
                )}
                <Field label="API Key">
                  <Input
                    value={addForm.api_key}
                    onChange={(e) => setAddForm((prev) => ({ ...prev, api_key: e.target.value }))}
                    placeholder="Paste API key"
                  />
                </Field>
                {addForm.provider_id === "azure" && (
                  <div className="flex gap-2 rounded-lg bg-fill-blue-50 dark:bg-fill-hex-1e3a5f/30 border border-edge-blue-200 dark:border-edge-blue-700/40 px-3 py-2.5">
                    <Info className="w-3.5 h-3.5 text-ink-blue-700 dark:text-ink-accent flex-shrink-0 mt-px" />
                    <div className="text-[11px] text-ink-blue-800 dark:text-ink-blue-300 leading-relaxed space-y-1.5">
                      <p>Only the API key is stored here. Per-model routing is configured on each model document in <strong>Model Config</strong>.</p>
                      <p>For Azure AI Foundry, set these fields on the model doc:</p>
                      <ul className="space-y-0.5 pl-1">
                        <li><span className="font-mono bg-fill-blue-100 dark:bg-fill-hex-1e3a5f px-1 py-px rounded text-[10px]">base_url</span>{" "}<span className="font-mono bg-fill-blue-100 dark:bg-fill-hex-1e3a5f px-1 py-px rounded text-[10px]">&quot;https://&lt;resource&gt;.services.ai.azure.com/openai/v1/&quot;</span></li>
                        <li><span className="font-mono bg-fill-blue-100 dark:bg-fill-hex-1e3a5f px-1 py-px rounded text-[10px]">use_responses_api</span>{" "}<span className="font-mono bg-fill-blue-100 dark:bg-fill-hex-1e3a5f px-1 py-px rounded text-[10px] text-ink-violet-600 dark:text-ink-violet-300">true</span>{" "}<span className="text-[10px] opacity-70">(boolean — Responses API only; omit or set <span className="font-mono font-bold">false</span> for Chat Completions)</span></li>
                      </ul>
                    </div>
                  </div>
                )}
                {(addForm.provider_id === "_custom" || addForm.provider_id === "ollama") && (
                  <Field
                    label="Base URL"
                    hint={
                      addForm.provider_id === "ollama"
                        ? "Ollama endpoint (e.g. http://localhost:11434 or your Cloud Run URL)."
                        : "OpenAI-compatible endpoint (e.g. https://api.example.com/v1)."
                    }
                  >
                    <Input
                      value={addForm.base_url}
                      onChange={(e) => setAddForm((prev) => ({ ...prev, base_url: e.target.value }))}
                      placeholder={
                        addForm.provider_id === "ollama"
                          ? "http://localhost:11434"
                          : addForm.provider_id === "azure"
                          ? "https://<resource>.openai.azure.com/"
                          : "https://api.example.com/v1"
                      }
                    />
                  </Field>
                )}
                <div className="flex gap-2 pt-1">
                  <CompactButton onClick={handleAddSave} disabled={addSaving || !canAddSave}>
                    {addSaving ? "Saving…" : "Save"}
                  </CompactButton>
                  <CompactButton
                    onClick={() => { setAdding(false); setAddForm({ provider_id: "", custom_id: "", api_key: "", base_url: "" }); }}
                  >
                    Cancel
                  </CompactButton>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </SettingsPageShell>
  );
};

export default ModelProviderConfigPage;
